import React, { useState, useEffect } from 'react';
import 'bootstrap/dist/css/bootstrap.min.css';
import { Helmet } from "react-helmet-async";
const ServiceNowDevelopment = () => {
  const phrases = [
    "Enterprise Workflows",
    "ServiceNow Development"
  ];

  const cardData = [
    { type: 'purple-clay', content: '⚙️' },
    { type: 'smooth-wave', content: '💻' },
    { type: 'abstract-blocks', content: '🔧' },
    { type: 'pink-sphere', content: '🚀' }
  ];

  const [currentPhraseIndex, setCurrentPhraseIndex] = useState(0);
  const [displayedText, setDisplayedText] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);
  const [cardIndex, setCardIndex] = useState(0);

  useEffect(() => {
    const currentPhrase = phrases[currentPhraseIndex];
    let timer;

    const handleTyping = () => {
      if (!isDeleting) {
        setDisplayedText(currentPhrase.substring(0, displayedText.length + 1));
        if (displayedText === currentPhrase) {
          timer = setTimeout(() => setIsDeleting(true), 1600);
          return;
        }
      } else {
        setDisplayedText(currentPhrase.substring(0, displayedText.length - 1));
        if (displayedText === '') {
          setIsDeleting(false);
          setCurrentPhraseIndex((prev) => (prev + 1) % phrases.length);
          return;
        }
      }

      const speed = isDeleting ? 30 : 80;
      timer = setTimeout(handleTyping, speed);
    };

    timer = setTimeout(handleTyping, 100);
    return () => clearTimeout(timer);
  }, [displayedText, isDeleting, currentPhraseIndex]);

  useEffect(() => {
    const cardTimer = setInterval(() => {
      setCardIndex((prev) => (prev + 1) % cardData.length);
    }, 1100);
    return () => clearInterval(cardTimer);
  }, []);

  // Intersection Observer for Scroll Animations
  useEffect(() => {
    const observerOptions = {
      root: null,
      rootMargin: '0px',
      threshold: 0.15
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('animate-visible');
        }
      });
    }, observerOptions);

    const animatedElements = document.querySelectorAll(
      '.scroll-anim-fade-in, .scroll-anim-fade-in-up, .scroll-anim-fade-in-left, .scroll-anim-fade-in-right, .scroll-anim-flip, .scroll-anim-zoom-in'
    );

    animatedElements.forEach((el) => observer.observe(el));

    return () => {
      animatedElements.forEach((el) => observer.unobserve(el));
    };
  }, []);

  // Content Data
  const coreServices = [
    { icon: "🛠️", title: "Custom Application Scoping", desc: "Design and deploy tailored scoped applications on the Now Platform to digitize unique corporate workflows." },
    { icon: "⚡", title: "Flow Designer Automation", desc: "Replace legacy script engines with low-code and complex programmatic multi-step logic pathways." },
    { icon: "🖥️", title: "Service Portal & Widget Crafting", desc: "Engineer highly responsive, branded, and intuitive user experiences leveraging modern Angular frameworks." },
    { icon: "🗄️", title: "Robust CMDB Architecture", desc: "Structure high-integrity Configuration Management Databases to eliminate visibility blindspots across assets." },
    { icon: "🔄", title: "IntegrationHub Configurations", desc: "Establish bidirectional pipelines linking your Now instance securely with external ERP, CRM, and cloud systems." },
    { icon: "📋", title: "ITIL Process Optimization", desc: "Fine-tune out-of-the-box Incident, Problem, Change, and Request workflows to maximize daily efficiency." },
    { icon: "🔐", title: "ACL & Data Security Shielding", desc: "Enforce meticulous access control lists and data masking layers ensuring airtight enterprise compliance." }
  ];

  const advancedServices = [
    { icon: "🤖", title: "Virtual Agent Deployment", desc: "Implement intelligent conversational bots and natural language understanding to automate user requests." },
    { icon: "🏥", title: "HRSD & Lifecycle Event Mapping", desc: "Modernize employee case management workflows from initial onboarding cycles to departure handoffs." },
    { icon: "📡", title: "ITOM & Event Orchestration", desc: "Configure horizontal discovery mechanisms, service mapping sweeps, and proactive health alerts." },
    { icon: "🛡️", title: "SecOps & IRM Governance", desc: "Weave threat intelligence signals, vulnerability responses, and risk controls directly into live workflows." },
    { icon: "📈", title: "Performance Analytics Setups", desc: "Construct advanced management KPI tracking indicators and historic trend matrices across operational teams." },
    { icon: "🧬", title: "Instance Consolidation & Upgrades", desc: "Execute safe instance health checks, skip-log evaluations, and smooth technical family platform version migrations." },
    { icon: "💼", title: "SPM Lifecycle Management", desc: "Unify project pipelines, resource scheduling structures, and demand prioritization pipelines in one place." },
    { icon: "🏗️", title: "MID Server Architecture Tuning", desc: "Deploy optimized, highly secure internal server communication pipelines behind corporate firewalls." },
    { icon: "🧪", title: "Automated Test Frameworks (ATF)", desc: "Build automated test suites to drastically accelerate upgrade validation speeds and safeguard customizations." }
  ];

  const technologies = {
    "Module Ecosystem": ["ITSM (Service Management)", "ITOM (Operations)", "HRSD (Employee Experience)", "CSM (Customer Service)"],
    "Development Frameworks": ["Client Scripts & Business Rules", "Flow Designer & Workflow Engines", "Service Portal (AngularJS / CSS)", "Script Includes & UI Actions"],
    "Platform Integration": ["REST / SOAP Web Services", "IntegrationHub Spokes", "MID Server Infrastructure", "JSON & XML Data Parsing"],
    "Data & Architecture": ["CMDB & Service Mapping", "Schema Design & Dict Customization", "Domain Separation Setup", "Instance Scan Engine"],
    "Quality Assurance": ["Automated Test Framework (ATF)", "Update Set Management", "Application Repository (App Repo)", "Source Control (Git) Integration"]
  };

  const processSteps = [
    { num: "01", title: "Instance Discovery", desc: "Evaluating current process friction points, structural technical debt, and target platform goals." },
    { num: "02", title: "Architectural Blueprinting", desc: "Mapping table schema extensions, access structures, integration landscapes, and data relationships." },
    { num: "03", title: "Agile Development", desc: "Writing clean server and client logic loops, portal interfaces, and tailored engine scripts." },
    { num: "04", title: "Orchestration & Plumbing", desc: "Binding external system endpoints, configuring spokes, and wiring automated data flows." },
    { num: "05", title: "ATF Testing & QA", desc: "Executing exhaustive automated test patterns and verifying access control boundary levels." },
    { num: "06", title: "Deployment Cycle", desc: "Migrating update sets or application repo tags smoothly into production tiers with zero disruption." },
    { num: "07", title: "Continuous Evolution", desc: "Tracking instance health scores, optimizing slow-running queries, and framing upgrade tracks." }
  ];

  const industries = [
    "Global Financial Infrastructure", "Healthcare Systems & Providers", "Telecommunications Providers", "E-Commerce & Retail Giants",
    "Energy & Critical Infrastructure", "High-Tech & SaaS Corporations", "Automotive & Manufacturing", "Government & Public Sectors",
    "Insurance & Risk Underwriting", "Logistics & Supply Chain Networks", "Professional Managed Services", "Aerospace Operations"
  ];

  const designBenefits = [
    "Unified Cross-Platform Workflows", "Drastic MTTR Service Decreases", "Airtight Internal Security Posture",
    "Highly Scalable Scoped Code Bases", "Eliminated Data Silo Architectures", "Modern Self-Service Portals",
    "Proactive System Outage Remediation", "Streamlined Automated IT Auditing"
  ];

  const whyChooseUs = [
    "Certified Now Platform Developers", "Robust Scoped App Architectural Prowess", "Modern UX/UI Front-End Portal Styling",
    "Flawless Complex Integration Experience", "Deep Understanding of ITIL Best Practices", "High-Performance Clean Code Paradigms",
    "Strategic Risk-Free Upgrade Navigation", "Transparent Delivery Sprint Cadences", "Proven Enterprise CMDB Clean-Ups",
    "Dedicated Post-Go-Live Support Matrix", "Native Automation-First Philosophy", "100% Core Platform Integrity Preservation"
  ];

  return (

    <>

      <Helmet>
        {/* Primary SEO */}
        <title>
          ServiceNow Development Services | ServiceNow Consulting & Implementation | Affsoft IT Solutions Pvt. Ltd.
        </title>

        <meta
          name="title"
          content="ServiceNow Development Services | ServiceNow Consulting & Implementation | Affsoft IT Solutions Pvt. Ltd."
        />

        <meta
          name="description"
          content="Affsoft IT Solutions Pvt. Ltd. offers expert ServiceNow development services, including ServiceNow consulting, implementation, customization, workflow automation, ITSM, ITOM, HRSD, CSM, integrations, and managed support to streamline business operations."
        />

        <meta
          name="keywords"
          content="ServiceNow Development, ServiceNow Services, ServiceNow Consulting, ServiceNow Implementation, ServiceNow Customization, ServiceNow Integration, ServiceNow ITSM, ServiceNow ITOM, ServiceNow HRSD, ServiceNow CSM, Workflow Automation, Enterprise Service Management, Digital Workflow Solutions, Service Portal Development, ServiceNow Managed Services, ServiceNow Support, ServiceNow Development Company, Affsoft IT Solutions Pvt. Ltd."
        />

        <meta name="robots" content="index, follow" />
        <meta name="author" content="Affsoft IT Solutions Pvt. Ltd." />
        <meta name="language" content="English" />
        <meta name="theme-color" content="#0d5c53" />

        {/* Canonical */}
        <link
          rel="canonical"
          href="https://www.affsoft.com/servicenow-development"
        />

        {/* Open Graph */}
        <meta property="og:type" content="website" />
        <meta
          property="og:site_name"
          content="Affsoft IT Solutions Pvt. Ltd."
        />
        <meta
          property="og:title"
          content="Professional ServiceNow Development Services | Affsoft IT Solutions Pvt. Ltd."
        />
        <meta
          property="og:description"
          content="Enhance your enterprise workflows with ServiceNow development services from Affsoft IT Solutions Pvt. Ltd. We specialize in ServiceNow implementation, customization, integrations, workflow automation, ITSM, and digital transformation solutions."
        />
        <meta
          property="og:url"
          content="https://www.affsoft.com/servicenow-development"
        />
        <meta
          property="og:image"
          content="https://www.affsoft.com/images/affsoft-logo.png"
        />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta
          name="twitter:title"
          content="ServiceNow Development Services | ServiceNow Consulting & Implementation | Affsoft IT Solutions Pvt. Ltd."
        />
        <meta
          name="twitter:description"
          content="Optimize business operations with expert ServiceNow development, consulting, implementation, workflow automation, and managed services from Affsoft IT Solutions Pvt. Ltd."
        />
        <meta
          name="twitter:image"
          content="https://www.affsoft.com/images/affsoft-logo.png"
        />
      </Helmet>
      <div className="servicenow-services-page bg-light" style={{ fontFamily: '"Inter", sans-serif', overflowX: 'hidden' }}>

        {/* ================= HERO SECTION ================= */}
        <div
          className="hero-container container-fluid d-flex align-items-center justify-content-center position-relative overflow-hidden"
          style={{
            backgroundImage: "url('./images/home.jpg')",
            backgroundRepeat: 'no-repeat',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            minHeight: '85vh',
            backgroundColor: '#0a101d' // dark contextual fallback
          }}
        >
          {/* Background Grid Lines */}
          <div className="grid-overlay position-absolute top-0 start-0 w-100 h-100" style={{ opacity: 0.12 }}>
            <div className="grid-line line-v-1"></div>
            <div className="grid-line line-v-2"></div>
            <div className="grid-line line-v-3"></div>
          </div>

          {/* Infinite Slow-Scrolling Marquee Text Background Wrapper */}
          <div className="marquee-wrapper select-none position-absolute w-100 scroll-anim-fade-in" style={{ top: '15%', opacity: 0.05 }}>

          </div>

          <div className="marquee-wrapper select-none position-absolute w-100 scroll-anim-fade-in">
            <div className="marquee-content text-white">
              <span>Enterprise Workflows &nbsp; and &nbsp;</span>
              <span>ServiceNow Development </span>
            </div>
          </div>

          {/* Main Content Layout */}
          <div className="container position-relative z-3 my-5">
            <div className="row align-items-center py-4">

              {/* Left Side Info */}
              <div className="col-12 col-md-5 text-start d-flex flex-column align-items-start hero-left-pane text-white mb-4 mb-md-0 scroll-anim-fade-in-left">
                <div className="asterisk-icon mb-3 text-success fs-3">✳</div>
                <h1 className="fw-bold mb-3 display-5">ServiceNow Development</h1>
                <p className="tagline mb-4 text-white" style={{ fontSize: '1rem', lineHeight: '1.6', opacity: 0.9 }}>
                  At Affsoft IT Solutions Pvt. Ltd., we transform disjointed corporate processes into powerful, automated operational workflows. Our advanced ServiceNow architectures deliver seamless business logic orchestration, stable third-party integrations, and robust application engineering to maximize your platform investment.
                </p>
                <button className="btn btn-outline-light d-flex align-items-center gap-2 px-4 py-2 rounded-pill fw-semibold">
                  <span>➔</span>
                  <span>About Affsoft</span>
                </button>
              </div>

              {/* Center Section */}
              <div className="col-12 col-md-4 text-center mb-4 mb-md-0 hero-center-pane scroll-anim-flip">
                <div className="typing-badge px-4 py-3 mx-auto bg-dark bg-opacity-75 rounded-pill border border-secondary shadow-lg d-inline-block">
                  <span className="typed-text text-white fw-mono fs-4">{displayedText}</span>
                  <span className="cursor text-success fs-4 animate-blink">|</span>
                </div>
              </div>

              {/* Right Section */}
              <div className="col-12 col-md-3 d-flex justify-content-center justify-content-md-end hero-right-pane scroll-anim-fade-in-right">
                <div className="glass-card-frame shadow-lg p-3 bg-white bg-opacity-10 rounded-4 border border-light" style={{ width: '220px', backdropFilter: 'blur(10px)' }}>
                  <div className={`inner-graphic-card graphic-${cardData[cardIndex].type} d-flex align-items-center justify-content-center rounded-3 bg-dark`} style={{ height: '180px' }}>
                    <span className="graphic-icon display-3">{cardData[cardIndex].content}</span>
                  </div>
                </div>
              </div>

            </div>
          </div>
        </div>

        {/* ================= CORE WORKFLOW SOLUTIONS ================= */}
        <section className="py-5 bg-white">
          <div className="container">
            <div className="text-center max-width-700 mx-auto mb-5 scroll-anim-fade-in-up">
              <span className="badge bg-primary px-3 py-2 rounded-pill mb-2 text-uppercase">Platform Optimization</span>
              <h2 className="fw-bold text-dark display-6">Clean Architecture Built for Seamless Scaling</h2>
              <p className="text-muted mt-3">
                Fragmented organizational processes hinder operational visibility and velocity. At Affsoft IT Solutions Pvt. Ltd., we build secure, maintainable core customizations that maximize efficiency while safeguarding platform upgradeability.
              </p>
            </div>

            <div className="row g-4">
              {coreServices.map((service, index) => (
                <div className="col-12 col-md-6 col-lg-4 scroll-anim-zoom-in" key={index} style={{ animationDelay: `${index * 0.1}s` }}>
                  <div className="card h-100 shadow-sm border-0 rounded-4 p-4 hover-translate transition-all">
                    <div className="fs-1 mb-3 text-primary">{service.icon}</div>
                    <h4 className="fw-bold fs-5 mb-2 text-dark">{service.title}</h4>
                    <p className="text-muted mb-0 small">{service.desc}</p>
                  </div>
                </div>
              ))}
            </div>

            {/* Workflow Architecture Checklist */}
            <div className="mt-5 p-4 rounded-4 bg-light border scroll-anim-fade-in-up">
              <h5 className="fw-bold text-center text-dark mb-4">Why Modernize Your ServiceNow Environment?</h5>
              <div className="row g-3">
                {designBenefits.map((benefit, index) => (
                  <div className="col-6 col-md-3 d-flex align-items-center gap-2" key={index}>
                    <span className="text-success fs-5">✓</span>
                    <span className="text-secondary small fw-medium">{benefit}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* ================= ADVANCED BUSINESS MODULES ================= */}
        <section className="py-5 bg-light">
          <div className="container">
            <div className="text-center max-width-700 mx-auto mb-5 scroll-anim-fade-in-up">
              <span className="badge bg-success px-3 py-2 rounded-pill mb-2 text-uppercase">Elite Orchestration</span>
              <h2 className="fw-bold text-dark display-6">Advanced Modules Programmed with Precision</h2>
              <p className="text-muted mt-3">
                We extend out-of-the-box system capabilities to form comprehensive operations nerve centers. From deep event routing rules to robust compliance maps, our engineering creates automated environments built to flex.
              </p>
            </div>

            <div className="row g-4">
              {advancedServices.map((service, index) => (
                <div className="col-12 col-md-6 col-lg-4 scroll-anim-zoom-in" key={index} style={{ animationDelay: `${index * 0.1}s` }}>
                  <div className="card h-100 shadow-sm border-0 rounded-4 p-4 bg-white hover-translate transition-all">
                    <div className="fs-1 mb-3 text-success">{service.icon}</div>
                    <h4 className="fw-bold fs-5 mb-2 text-dark">{service.title}</h4>
                    <p className="text-muted mb-0 small">{service.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ================= TECHNOLOGIES SECTION ================= */}
        <section className="py-5 bg-dark text-white">
          <div className="container">
            <div className="text-center mb-5 scroll-anim-fade-in-up">
              <span className="badge bg-success text-white px-3 py-2 rounded-pill mb-2 text-uppercase fw-bold">Platform Tech Stack</span>
              <h2 className="fw-bold display-6">ServiceNow Ecosystem Subsystems We Leverage</h2>
              <p className="text-light-50">We build and validate complex system tracks using native, robust developer utility paradigms.</p>
            </div>

            <div className="row g-4 justify-content-center">
              {Object.entries(technologies).map(([category, list], index) => (
                <div className="col-12 col-sm-6 col-md-4 col-lg scroll-anim-fade-in" key={index}>
                  <div className="bg-secondary bg-opacity-25 p-4 rounded-4 h-100 border border-secondary border-opacity-50">
                    <h5 className="fw-bold text-success mb-3 border-bottom border-secondary pb-2">{category}</h5>
                    <ul className="list-unstyled mb-0">
                      {list.map((tech, i) => (
                        <li key={i} className="mb-2 d-flex align-items-center gap-2 small">
                          <span className="text-success opacity-75">•</span> {tech}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ================= ROADMAP PROCESS ================= */}
        <section className="py-5 bg-white">
          <div className="container">
            <div className="text-center mb-5 scroll-anim-fade-in-up">
              <span className="badge bg-secondary px-3 py-2 rounded-pill mb-2 text-uppercase">Implementation Track</span>
              <h2 className="fw-bold text-dark display-6">Our Engineered Development Lifecycle</h2>
              <p className="text-muted">A highly methodical milestone matrix guiding abstract business logic into clean instance tools.</p>
            </div>

            <div className="row g-3">
              {processSteps.map((step, index) => (
                <div className="col-12 col-md-6 col-lg scroll-anim-fade-in-up" key={index} style={{ animationDelay: `${index * 0.15}s` }}>
                  <div className="p-4 rounded-4 bg-light border-start border-success border-4 h-100 shadow-sm">
                    <div className="fw-bold text-success display-6 mb-2">{step.num}</div>
                    <h5 className="fw-bold text-dark fs-6">{step.title}</h5>
                    <p className="text-muted small mb-0">{step.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ================= INDUSTRIES SERVED ================= */}
        <section className="py-5 bg-light">
          <div className="container text-center">
            <h2 className="fw-bold text-dark mb-4 display-6 scroll-anim-fade-in-up">Industries We Empower</h2>
            <div className="d-flex flex-wrap justify-content-center gap-2 max-width-900 mx-auto scroll-anim-zoom-in">
              {industries.map((industry, index) => (
                <span className="badge bg-white text-dark border px-3 py-2 rounded-pill shadow-sm fs-7 fw-medium" key={index}>
                  🏢 {industry}
                </span>
              ))}
            </div>
          </div>
        </section>

        {/* ================= WHY CHOOSE AFFSOFT ================= */}
        <section className="py-5 bg-white border-top">
          <div className="container">
            <div className="text-center mb-5 scroll-anim-fade-in-up">
              <h2 className="fw-bold text-dark display-6">Why Partner with Affsoft IT Solutions Pvt. Ltd.?</h2>
              <p className="text-muted">We combine programmatic strictness with precise instance health strategies to scale business tools safely.</p>
            </div>

            <div className="row g-3">
              {whyChooseUs.map((reason, index) => (
                <div className="col-12 col-md-6 col-lg-4 scroll-anim-fade-in-left" key={index} style={{ animationDelay: `${(index % 3) * 0.1}s` }}>
                  <div className="d-flex align-items-center gap-3 p-3 bg-light rounded-3 h-100">
                    <span className="text-success fs-4">🛡️</span>
                    <span className="text-dark fw-semibold small">{reason}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Custom Styles Injection */}
        <style>{`
        .hover-translate {
          transition: transform 0.2s ease, box-shadow 0.2s ease;
        }
        .hover-translate:hover {
          transform: translateY(-5px);
          box-shadow: 0 10px 20px rgba(0,0,0,0.1) !important;
        }
        .text-light-50 {
          color: rgba(255, 255, 255, 0.7);
        }
        .max-width-700 {
          max-width: 700px;
        }
        .max-width-900 {
          max-width: 900px;
        }
        .animate-blink {
          animation: blink 1s step-end infinite;
        }
        @keyframes blink {
          from, to { color: transparent }
          50% { color: inherit }
        }

        /* --- Scroll Animation Core --- */
        .scroll-anim-fade-in,
        .scroll-anim-fade-in-up,
        .scroll-anim-fade-in-left,
        .scroll-anim-fade-in-right,
        .scroll-anim-flip,
        .scroll-anim-zoom-in {
          opacity: 0;
          will-change: transform, opacity;
          transition: all 0.8s cubic-bezier(0.25, 0.46, 0.45, 0.94);
        }

        /* Visibility Trigger via IntersectionObserver */
        .animate-visible {
          opacity: 1 !important;
          transform: none !important;
        }

        /* --- Animations Configurations --- */
        .scroll-anim-fade-in-up {
          transform: translateY(40px);
        }
        .scroll-anim-fade-in-left {
          transform: translateX(-50px);
        }
        .scroll-anim-fade-in-right {
          transform: translateX(50px);
        }
        .scroll-anim-zoom-in {
          transform: scale(0.85);
        }
        .scroll-anim-flip {
          transform: perspective(400px) rotateY(90deg);
          transition: all 1s cubic-bezier(0.175, 0.885, 0.32, 1.275);
        }
        
        .scroll-anim-flip.animate-visible {
          transform: perspective(400px) rotateY(0deg) !important;
        }
      `}</style>

      </div>

    </>
  );
};

export default ServiceNowDevelopment;